/**
 * Journal Block Service
 * CRUD operations for journal_blocks table
 */

import { supabase } from '@/integrations/supabase/client';
import type { JournalBlock, CreateBlockInput, UpdateBlockInput } from '@/types/journal';

/**
 * Get all blocks for a journal
 */
export async function getJournalBlocks(journalId: string): Promise<JournalBlock[]> {
  const { data, error } = await supabase
    .from('journal_blocks')
    .select('*')
    .eq('journal_id', journalId)
    .order('position', { ascending: true });

  if (error) {
    console.error('Error fetching journal blocks:', error);
    throw error;
  }

  return data || [];
}

/**
 * Get a single block by ID
 */
export async function getJournalBlock(blockId: string): Promise<JournalBlock | null> {
  const { data, error } = await supabase
    .from('journal_blocks')
    .select('*')
    .eq('id', blockId)
    .single();

  if (error) {
    console.error('Error fetching journal block:', error);
    throw error;
  }

  return data;
}

/**
 * Create a new block
 */
export async function createJournalBlock(input: CreateBlockInput): Promise<JournalBlock> {
  const insertData = {
    journal_id: input.journal_id,
    block_type: input.block_type,
    title: input.title,
    position: input.position,
    content: (input.content || {}) as unknown,
    is_collapsed: input.is_collapsed ?? false,
  };
  
  const { data, error } = await supabase
    .from('journal_blocks')
    .insert(insertData as any)
    .select()
    .single();

  if (error) {
    console.error('Error creating journal block:', error);
    throw error;
  }

  return data;
}

/**
 * Update a block
 * Note: updated_at is automatically updated by trigger
 */
export async function updateJournalBlock(
  blockId: string,
  updates: UpdateBlockInput
): Promise<JournalBlock> {
  const { data, error } = await supabase
    .from('journal_blocks')
    .update(updates as any)
    .eq('id', blockId)
    .select()
    .single();

  if (error) {
    console.error('Error updating journal block:', error);
    throw error;
  }

  return data;
}

/**
 * Update block content only (debounced updates)
 */
export async function updateBlockContent(
  blockId: string,
  content: Record<string, any>
): Promise<void> {
  const { error } = await supabase
    .from('journal_blocks')
    .update({ content })
    .eq('id', blockId);

  if (error) {
    console.error('Error updating block content:', error);
    throw error;
  }
}

/**
 * Delete a block
 */
export async function deleteJournalBlock(blockId: string): Promise<void> {
  const { error } = await supabase
    .from('journal_blocks')
    .delete()
    .eq('id', blockId);

  if (error) {
    console.error('Error deleting journal block:', error);
    throw error;
  }
}

/**
 * Reorder blocks by updating positions
 * Performs batch update for optimal performance
 */
export async function reorderBlocks(
  blocks: Array<{ id: string; position: number }>
): Promise<void> {
  // Update positions in a single transaction using RPC if needed
  // For now, we'll update them individually
  const updates = blocks.map((block) =>
    supabase
      .from('journal_blocks')
      .update({ position: block.position })
      .eq('id', block.id)
  );

  const results = await Promise.all(updates);

  const errors = results.filter((r) => r.error).map((r) => r.error);
  if (errors.length > 0) {
    console.error('Error reordering blocks:', errors);
    throw new Error('Failed to reorder some blocks');
  }
}

/**
 * Duplicate a block
 */
export async function duplicateBlock(
  blockId: string,
  newPosition: number
): Promise<JournalBlock> {
  // Fetch the original block
  const original = await getJournalBlock(blockId);
  if (!original) {
    throw new Error('Block not found');
  }

  // Create a new block with the same content
  const { data, error } = await supabase
    .from('journal_blocks')
    .insert({
      journal_id: original.journal_id,
      block_type: original.block_type,
      title: `${original.title} (Copy)`,
      position: newPosition,
      content: original.content,
      is_collapsed: false,
    })
    .select()
    .single();

  if (error) {
    console.error('Error duplicating block:', error);
    throw error;
  }

  return data;
}





